// Types
import type { Ref } from 'vue';
import type {
  TAutomatedEmailUserTemplateApi,
  TEmailTemplate,
  TTemplatePayload,
  TTrigger,
} from '~/modules/opportunities/automated-email/types';
import type { TUserTemplateUpdatePayload } from '~/modules/opportunities/automated-email/settings/types';

// Composables
import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';
import { useAutomatedEmailPatch } from '~/modules/opportunities/automated-email/composables/use-automated-email-patch';

// Models
import { TriggersModel } from '~/modules/opportunities/automated-email/settings/models/triggers';

// Utils
import { ensureAutomatedEmailTriggersAndTemplates } from '~/modules/opportunities/automated-email/settings/utils/ensure-automated-email-triggers-templates';
import { mapUserTriggersAndTemplatesToTriggers } from '~/modules/opportunities/automated-email/settings/utils/triggers-api-map';

const applyUpdatedUserTemplate = (
  template: TEmailTemplate,
  updated: TAutomatedEmailUserTemplateApi
): void => {
  template.title = updated.subject;
  template.content = updated.content;
  template.is_linked = updated.is_active;
};

export const useTriggers = () => {
  const model = new TriggersModel();
  const { notifySuccess, notifyFailure } = useNotifyResource();
  const { patchId, saleType } = useAutomatedEmailPatch();

  const triggers: Ref<TTrigger[]> = ref<TTrigger[]>([]);
  const originalTemplateStates: Ref<Map<number, boolean>> = ref<Map<number, boolean>>(new Map());
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const isSavingTemplates: Ref<boolean> = ref<boolean>(false);

  const setTriggers = (value: TTrigger[]): void => {
    triggers.value = value;
  };

  const setOriginalTemplateStates = (value: Map<number, boolean>): void => {
    originalTemplateStates.value = value;
  };

  const setIsLoading = (value: boolean): void => {
    isLoading.value = value;
  };

  const setIsSavingTemplates = (value: boolean): void => {
    isSavingTemplates.value = value;
  };

  const snapshotTemplateStates = (): void => {
    const map: Map<number, boolean> = new Map<number, boolean>();

    triggers.value.forEach((trigger: TTrigger) => {
      trigger.templates.forEach((template: TEmailTemplate) => {
        map.set(template.id, template.is_linked);
      });
    });

    setOriginalTemplateStates(map);
  };

  const fetchTriggers = async (): Promise<void> => {
    if (patchId.value === undefined) {
      setTriggers([]);
      setOriginalTemplateStates(new Map());

      return;
    }

    setIsLoading(true);

    try {
      const { triggers: apiTriggers, templates: apiTemplates } =
        await ensureAutomatedEmailTriggersAndTemplates(model, patchId.value, saleType.value);

      setTriggers(mapUserTriggersAndTemplatesToTriggers(apiTriggers, apiTemplates));
      snapshotTemplateStates();
    } finally {
      setIsLoading(false);
    }
  };

  const isLastLinkedTemplate = (templateId: number): boolean => {
    const linked: TEmailTemplate[] = [];

    triggers.value.forEach((trigger: TTrigger) => {
      trigger.templates.forEach((t: TEmailTemplate) => {
        if (t.is_linked) {
          linked.push(t);
        }
      });
    });

    return linked.length === 1 && linked[0].id === templateId;
  };

  const toggleTemplate = (triggerId: number, templateId: number, value: boolean): void => {
    const trigger: TTrigger | undefined = triggers.value.find(
      (t: TTrigger): boolean => t.id === triggerId
    );

    if (!trigger) {
      return;
    }

    if (!value && isLastLinkedTemplate(templateId)) {
      return;
    }

    const template: TEmailTemplate | undefined = trigger.templates.find(
      (t: TEmailTemplate): boolean => t.id === templateId
    );

    if (!template) {
      return;
    }

    template.is_linked = value;
  };

  const updateTemplate = async (
    triggerId: number,
    templateId: number,
    title: string,
    content: string
  ): Promise<void> => {
    const trigger: TTrigger | undefined = triggers.value.find(
      (t: TTrigger): boolean => t.id === triggerId
    );

    if (!trigger) {
      return;
    }

    const template: TEmailTemplate | undefined = trigger.templates.find(
      (t: TEmailTemplate): boolean => t.id === templateId
    );

    if (!template) {
      return;
    }

    const payload: TUserTemplateUpdatePayload = {
      subject: title,
      content,
    };

    try {
      const updated: TAutomatedEmailUserTemplateApi = await model.updatePatchTemplate(
        templateId,
        payload
      );

      applyUpdatedUserTemplate(template, updated);
      notifySuccess();
    } catch (error: unknown) {
      notifyFailure();

      throw error;
    }
  };

  /** Email Templates section Save: POST `patch-templates/activate` for toggles (`is_linked`) that changed since last fetch/save. */
  const saveTemplates = async (): Promise<void> => {
    setIsSavingTemplates(true);

    const changedTemplates: TTemplatePayload[] = [];

    triggers.value.forEach((trigger: TTrigger) => {
      trigger.templates.forEach((template: TEmailTemplate) => {
        const original: boolean | undefined = originalTemplateStates.value.get(template.id);

        if (original !== template.is_linked) {
          changedTemplates.push({ id: template.id, is_active: template.is_linked });
        }
      });
    });

    if (changedTemplates.length === 0) {
      setIsSavingTemplates(false);

      return;
    }

    try {
      await model.activatePatchTemplates(
        changedTemplates.map((t: TTemplatePayload) => ({
          id: t.id,
          value: t.is_active,
        }))
      );

      snapshotTemplateStates();
      notifySuccess();
    } finally {
      setIsSavingTemplates(false);
    }
  };

  return {
    patchId,
    triggers,
    isLoading,
    isSavingTemplates,
    fetchTriggers,
    toggleTemplate,
    updateTemplate,
    saveTemplates,
    isLastLinkedTemplate,
  };
};
